import { useEffect, useMemo, useRef, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';
import axiosScoring from 'config/axiosScoring';

const useAdminScoreData = (
  textId,
  analyzeEndpointUrl,
  analysisPayload,
  optionsOrDelay = 4000,
  legacySpinnerDelay = 2000,
  detailsEndpointPrefix = '/texts'
) => {
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const hasInitializedRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  const payloadKey = useMemo(() => {
    if (!analysisPayload) return '';

    try {
      const { html, text, keyword, gap, maillage_interne, language, country } =
        analysisPayload || {};
      return JSON.stringify({
        html,
        text,
        keyword,
        gap,
        maillage_interne,
        language,
        country,
      });
    } catch (_) {
      return JSON.stringify(analysisPayload);
    }
  }, [analysisPayload]);

  const lastPayloadKeyRef = useRef('');

  const options = useMemo(() => {
    if (typeof optionsOrDelay === 'number') {
      return {
        analyzeDelay: optionsOrDelay,
        initialSpinnerDelay: 2000,
        analyzeSpinnerDelay: legacySpinnerDelay,
      };
    }
    return {
      analyzeDelay: optionsOrDelay?.analyzeDelay ?? 4000,
      initialSpinnerDelay: optionsOrDelay?.initialSpinnerDelay ?? 2000,
      analyzeSpinnerDelay: optionsOrDelay?.analyzeSpinnerDelay ?? 2000,
      disableAnalyze: optionsOrDelay?.disableAnalyze ?? false,
    };
  }, [optionsOrDelay, legacySpinnerDelay]);

  useEffect(() => {
    if (!textId || hasInitializedRef.current) return;

    let isMounted = true;
    let spinnerTimer;

    const fetchInitial = async () => {
      let contentScoreFound = false;
      try {
        if (options.initialSpinnerDelay > 0) {
          spinnerTimer = setTimeout(() => {
            if (isMounted) setLoading(true);
          }, options.initialSpinnerDelay);
        }

        const response = await axiosPrivate.get(
          `${detailsEndpointPrefix}/${textId}/fullDetails`
        );

        if (!isMounted) return;

        if (response?.status === 200 && response?.data?.contentScore) {
          setScoreData(response.data.contentScore);
          setError(null);
          contentScoreFound = true;
        } else {
          setScoreData({});
          setError(null);
          contentScoreFound = false;
        }
      } catch (err) {
        if (!isMounted) return;

        const errorMessage = err.response?.data || err.message || 'Failed to fetch score data';
        const isTextNotExistError = typeof errorMessage === 'string' &&
          (errorMessage.toLowerCase().includes("this text doesn't exist") ||
            errorMessage.toLowerCase().includes("texte n'existe pas"));

        if (isTextNotExistError) {
          setScoreData({});
          setError(null);
        } else {
          setError(errorMessage);
        }
        contentScoreFound = false;
      } finally {
        clearTimeout(spinnerTimer);
        if (isMounted) setLoading(false);
        hasInitializedRef.current = true;
        isInitialLoadRef.current = false;

        if (isMounted) setInitialized(true);

        // If we didn't get a content score, force an analysis run immediately
        lastPayloadKeyRef.current = contentScoreFound ? payloadKey : '';
      }
    };

    fetchInitial();

    return () => {
      isMounted = false;
      clearTimeout(spinnerTimer);
    };
  }, [textId, options.initialSpinnerDelay, detailsEndpointPrefix]);

  useEffect(() => {
    if (options.disableAnalyze) {
      return;
    }

    if (!analysisPayload || !payloadKey) return;

    if (textId && !hasInitializedRef.current) {
      return;
    }

    if (!textId && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setInitialized(true);
    }

    const hasPayloadChanged = payloadKey !== lastPayloadKeyRef.current;

    if (lastPayloadKeyRef.current === '' && hasInitializedRef.current) {
      if (textId) {
        lastPayloadKeyRef.current = payloadKey;
        return;
      }
    }

    if (!hasPayloadChanged) {
      return;
    }

    let isMounted = true;
    let analyzeTimer;
    let spinnerTimer;

    const runAnalyze = async () => {
      try {
        setIsAnalyzing(true);

        if (options.analyzeSpinnerDelay > 0) {
          spinnerTimer = setTimeout(() => {
            if (isMounted) setLoading(true);
          }, options.analyzeSpinnerDelay);
        }

        const response = await axiosScoring.post(
          analyzeEndpointUrl,
          analysisPayload,
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!isMounted) return;

        setScoreData(response?.data || null);
        setError(null);
      } catch (err) {
        if (!isMounted) return;

        setError(err);
      } finally {
        clearTimeout(spinnerTimer);
        if (isMounted) setLoading(false);
        if (isMounted) setIsAnalyzing(false);
      }
    };

    analyzeTimer = setTimeout(runAnalyze, options.analyzeDelay);

    lastPayloadKeyRef.current = payloadKey;

    return () => {
      isMounted = false;
      clearTimeout(analyzeTimer);
      clearTimeout(spinnerTimer);
    };
  }, [
    payloadKey,
    analyzeEndpointUrl,
    textId,
    options.analyzeDelay,
    options.analyzeSpinnerDelay,
    options.disableAnalyze,
  ]);

  useEffect(() => {
    lastPayloadKeyRef.current = '';
    hasInitializedRef.current = false;
    isInitialLoadRef.current = true;
    setError(null);
    setInitialized(false);
    setIsAnalyzing(false);
  }, [textId]);

  return { scoreData, loading, error, initialized, isAnalyzing };
};

export default useAdminScoreData;
